import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Calendar as CalendarIcon, Clock, MapPin, Star, Home, Store as StoreIcon } from 'lucide-react';
import { toast } from 'sonner';
import ServiceBooking from './ServiceBooking';
import StarRating from './StarRating';

export default function ServiceProductView({ product, store }) {
    const { addToCart, clearCart, setShippingAddress } = useCart();
    const navigate = useNavigate();
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [bookingData, setBookingData] = useState(null);

    // Determine current price
    const currentPrice = product.discount_price || product.price;
    const hasDiscount = !!product.discount_price;
    const discountPercent = hasDiscount
        ? Math.round(((product.price - product.discount_price) / product.price) * 100)
        : 0;

    // Images
    const images = product.images && product.images.length > 0
        ? product.images
        : ["https://images.pexels.com/photos/3993452/pexels-photo-3993452.jpeg?auto=compress&cs=tinysrgb&w=800"];

    // Service duration display
    const durationText = product.service_duration
        ? `${product.service_duration} min`
        : 'Durée variable';

    // Location types
    const locationTypes = {
        'provider_shop': { label: 'À la boutique', icon: StoreIcon },
        'client_home': { label: 'À domicile', icon: Home },
        'both': { label: 'Boutique ou Domicile', icon: MapPin }
    };

    const locationType = locationTypes[product.service_location_type] || locationTypes.both;

    const handleBooking = (booking) => {
        setBookingData(booking);

        // Calculate total price for this specific booking
        const basePrice = product.discount_price || product.price;
        const total = basePrice + (booking.travelFee || 0);

        // Redirect directly to Service Checkout with state
        // This bypasses the general cart entirely as requested
        navigate('/service-checkout', {
            state: {
                product,
                booking,
                totalPrice: total
            }
        });
    };

    return (
        <div className="grid md:grid-cols-2 gap-8">
            {/* LEFT: Image Gallery */}
            <div className="space-y-4">
                {/* Main Image */}
                <div className="relative aspect-square bg-gradient-to-br from-orange-50 to-pink-50 rounded-xl overflow-hidden border border-gray-200">
                    {hasDiscount && (
                        <Badge className="absolute top-4 left-4 bg-red-600 text-white z-10">
                            -{discountPercent}%
                        </Badge>
                    )}
                    <Badge className="absolute top-4 right-4 bg-purple-600 text-white z-10">
                        Service
                    </Badge>
                    <img
                        src={images[currentImageIndex]}
                        alt={product.title}
                        className="w-full h-full object-cover"
                    />
                </div>

                {/* Thumbnails */}
                {images.length > 1 && (
                    <div className="flex gap-2 overflow-x-auto">
                        {images.map((img, idx) => (
                            <button
                                key={idx}
                                onClick={() => setCurrentImageIndex(idx)}
                                className={`flex-shrink-0 w-20 h-20 rounded-lg border-2 overflow-hidden ${currentImageIndex === idx ? 'border-purple-500' : 'border-gray-200'
                                    }`}
                            >
                                <img src={img} alt="" className="w-full h-full object-cover" />
                            </button>
                        ))}
                    </div>
                )}

                {/* Service Info Cards */}
                <div className="grid grid-cols-2 gap-3">
                    <Card className="p-4">
                        <div className="flex items-center gap-2 text-purple-600 mb-1">
                            <Clock className="h-5 w-5" />
                            <span className="font-semibold text-sm">Durée</span>
                        </div>
                        <p className="text-gray-900 font-medium">{durationText}</p>
                    </Card>

                    <Card className="p-4">
                        <div className="flex items-center gap-2 text-purple-600 mb-1">
                            <locationType.icon className="h-5 w-5" />
                            <span className="font-semibold text-sm">Lieu</span>
                        </div>
                        <p className="text-gray-900 font-medium text-sm">{locationType.label}</p>
                    </Card>
                </div>
            </div>

            {/* RIGHT: Service Info & Booking */}
            <div className="space-y-6">
                {/* Title & Rating */}
                <div>
                    {store && (
                        <Link to={`/boutique/${store.slug}`} className="text-purple-600 font-semibold hover:underline mb-1 inline-block">
                            {store.name}
                        </Link>
                    )}
                    <h1 className="text-3xl font-bold text-gray-900 mb-2">{product.title}</h1>
                    <div className="flex items-center gap-2">
                        <StarRating
                            rating={product.average_rating || 0}
                            totalReviews={product.total_reviews || 0}
                            size="lg"
                            showCount={true}
                        />
                    </div>
                </div>

                {/* Price */}
                <div className="flex items-baseline gap-3">
                    <span className="text-4xl font-bold text-purple-600">{currentPrice.toFixed(2)} HTG</span>
                    {hasDiscount && (
                        <span className="text-xl text-gray-500 line-through">{product.price.toFixed(2)} HTG</span>
                    )}
                </div>

                {/* Badges */}
                <div className="flex flex-wrap gap-2">
                    {product.is_featured && (
                        <Badge className="bg-orange-500">Top Service</Badge>
                    )}
                    <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
                        Réservation requise
                    </Badge>
                </div>

                {/* Description */}
                <div className="prose prose-sm">
                    <p className="text-gray-700 leading-relaxed">{product.description}</p>
                </div>

                {/* Service Booking Component */}
                <Card className="border-purple-200 bg-purple-50/30">
                    <CardHeader className="pb-3">
                        <CardTitle className="text-lg flex items-center gap-2">
                            <CalendarIcon className="h-5 w-5 text-purple-600" />
                            Réserver ce service
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <ServiceBooking
                            product={product}
                            price={currentPrice}
                            store={store}
                            onAddToCart={handleBooking}
                        />
                    </CardContent>
                </Card>

                {/* Store Info */}
                {store && (
                    <Card className="p-4 bg-gray-50">
                        <div className="flex items-start gap-3">
                            {store.logo_url ? (
                                <img
                                    src={store.logo_url}
                                    alt={store.name}
                                    className="w-12 h-12 rounded-full object-cover"
                                />
                            ) : (
                                <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                                    <StoreIcon className="h-6 w-6 text-purple-600" />
                                </div>
                            )}
                            <div className="flex-1">
                                <h3 className="font-semibold text-gray-900">{store.name}</h3>
                                <p className="text-sm text-gray-600">{store.description}</p>
                                {store.address && (
                                    <div className="flex items-center gap-1 mt-1 text-xs text-gray-500">
                                        <MapPin className="h-3 w-3" />
                                        <span>{store.address}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </Card>
                )}
            </div>
        </div>
    );
}
